/**
 * aggregateChartModel (#2457, rewritten by HOF-6) — pure recharts shaping for
 * the Preisspiegel price chart. Turns {@link AggregateModel}s into a rows array
 * plus line specs; `PriceChart` stays a thin declarative view, because recharts
 * does not render under jsdom and this is where the behaviour can be tested.
 *
 * ── The axis: ONE FIXED YEAR, and nothing can move it (HOF-6 §1) ─────────────
 *
 * The window is {@link fixedYearDomain}: 365 days wide with "Heute" pinned at
 * 20 % from the right edge. It is a pure function of `today` — not of the data,
 * not of a period preset, not of anything the reader clicks. The zoom/pan
 * toolbar (#2436/#2543) and the `1T…Max` ZEITRAUM presets are both gone with it.
 *
 * That is a real trade and worth naming. What it costs: history older than ten
 * months is no longer reachable on this chart at all. What it buys: two readers
 * looking at the same item on the same day see the SAME PICTURE, so a price
 * someone points at is a price the other one can find — which a zoomable axis
 * could never promise. Older seasons come back as OVERLAYS (`priorModels`),
 * which is the comparison the pan was actually being used for, drawn so the two
 * years line up day-of-year instead of being scrolled between.
 *
 * The right-hand fifth is not padding: it is where the dashed carry-forward past
 * "Heute" lives, so the projection has room to be seen without the reader
 * panning to it.
 *
 * ── The lines: side × channel, drawn together (HOF-6 §5) ────────────────────
 *
 * Kauf/Verkauf used to be a SWITCHER above this model, so it only ever saw one
 * side. Both are now drawn at once and told apart in the legend, which makes a
 * side a line dimension here. Each line reads one {@link AggregateModel} group,
 * so a purchase price is never folded into a sale price — the model cannot even
 * express that.
 *
 * ── What is gone, and why it left ───────────────────────────────────────────
 *
 *  - **the p10–p90 whisker** (HOF-6 §2). It existed to show spread around a
 *    MEDIAN. The line is a volume-weighted average now, whose spread story is
 *    the drill-down panel's "Spanne (Min–Max)" over the very rows it lists;
 *    a percentile band around a weighted mean would have been a statistic of two
 *    minds. No row carries an array any more, which is what `errKey` was.
 *  - **the OHLC candle mode** (HOF-6 §4). Candles need an open and a close, and
 *    a `price_observation` carries a date but no clock — so within a bucket
 *    there was no true order to derive a direction from, and #2778 had already
 *    had to degenerate a day-grain candle to a doji for exactly that reason. The
 *    users did not read them. The dense spine, the `<Bar shape>` geometry and
 *    the zoom floor that existed only to keep a candle from flooding the plot
 *    went with them.
 *  - **the per-point dots** (HOF-6 §2). One dot per observed day over a fixed
 *    year is a dotted rule, not a mark. The view draws a dot for the SELECTED
 *    bucket only — where it means something.
 */
import { CHANNEL_LABELS, SIDE_LABELS, type Channel, type PriceSide } from './catalog';
import { epochDayToIso, type Grain } from './grain';
import { toEpochDay } from './priceAt';
import { NO_CHANNEL, groupKey, type AggregateModel, type BucketAgg, type ChannelSlot, type GroupKey } from './aggregate';
import type { TimeDomain } from '../../../components/charts/timeDomain';

export type ChartView = 'alle' | 'kanal';

const DAY_MS = 86_400_000;

/**
 * A run of observations longer than this many days without a single one breaks
 * the observed line (#2543). Six weeks: comfortably longer than any within-season
 * pause (a weekly-delivered crop skips at most a few weeks for weather) and
 * comfortably shorter than any off-season, which for the berry and asparagus
 * items this app tracks runs three months or more.
 *
 * **#2652 gave the threshold a second job.** It is still the point at which the
 * OBSERVED line stops — that never changes, and it is why the constant exists —
 * but a break between two observed segments is now also where a carry-forward
 * {@link AggLineBridge} is drawn, when (and only when) the read behind the model
 * is complete. Deliberately ONE number governing both: a second "bridge after N
 * days" threshold would let the chart show a stretch that is neither drawn as
 * observed nor explained as empty, which is the ambiguity the footer copy exists
 * to close.
 *
 * Measured in DAYS regardless of grain, so the rule means the same thing at 1T
 * and 1M — at month grain two adjacent buckets are ~30 days apart and never
 * break, which is correct: a monthly series has no off-season resolution to lose.
 */
export const GAP_BREAK_DAYS = 42;

/** The chart's fixed window width, in days (HOF-6 §1: "the current fixed year"). */
export const YEAR_WINDOW_DAYS = 365;

/**
 * Where "Heute" sits, as a fraction in from the RIGHT edge (HOF-6 §1).
 *
 * The remaining fifth of the plot is the forward room the dashed carry-forward
 * needs. Pinning the mark rather than the calendar year is what makes the window
 * a rolling one: on 2 January a fixed Jan–Dec year would be 99 % empty.
 */
export const TODAY_FROM_RIGHT = 0.2;

/**
 * The chart's window: one year wide, "Heute" at {@link TODAY_FROM_RIGHT} in from
 * the right edge.
 *
 * Rounded to whole days on both sides, so every boundary is a real calendar day
 * and the axis ticks (which are calendar boundaries) cannot land a fraction of a
 * day outside the domain.
 */
export function fixedYearDomain(todayEpochDay: number): TimeDomain {
  const forward = Math.round(YEAR_WINDOW_DAYS * TODAY_FROM_RIGHT);
  const hi = todayEpochDay + forward;
  return { lo: (hi - YEAR_WINDOW_DAYS) * DAY_MS, hi: hi * DAY_MS };
}

/** ISO ``YYYY-MM-DD`` → UTC epoch MILLISECONDS at 00:00 of that day. */
export function tsOfIso(iso: string): number {
  return toEpochDay(iso) * DAY_MS;
}

/** UTC epoch ms → the ISO ``YYYY-MM-DD`` day it falls in. */
export function isoOfTs(ts: number): string {
  return epochDayToIso(Math.floor(ts / DAY_MS));
}

export type AggChartRow = {
  /** Bucket START as ISO YYYY-MM-DD — the day-panel / ``?d=`` key. */
  date: string;
  /** UTC epoch MILLISECONDS at 00:00 of `date` — the XAxis dataKey (#2543). */
  ts: number;
} & Record<string, number | string | null>;

/**
 * One contiguous run of observed buckets — the unit a `<Line>` is drawn over
 * (#2543). Two segments of the same line are two different recharts elements
 * over two different dataKeys, so recharts *cannot* draw a path between them:
 * there is no `connectNulls` value that could fabricate a winter plateau.
 */
export interface AggLineSegment {
  /** recharts dataKey for this segment's observed prices. */
  key: string;
  /** First observed bucket in the segment, epoch ms. */
  fromTs: number;
  /** Last observed bucket in the segment, epoch ms. */
  toTs: number;
}

/**
 * One off-season carry-forward across a gap between two observed segments
 * (#2652) — the preceding segment's last price, held flat to the bucket the next
 * segment starts on.
 *
 * **One `<Line>` per bridge, exactly as for a segment**, and for the same
 * reason: a single shared bridge key carrying every gap of a line would be
 * joined straight across the observed SEASON between two gaps (`connectNulls`)
 * or would depend on null placement to break in the right places.
 *
 * It contributes to nothing else: not the price, not the panel, not the `?d=`
 * resolution. It adds no rows at all, only values on rows that already exist.
 */
export interface AggLineBridge {
  key: string;
  /** Last observed bucket of the segment BEFORE the gap, epoch ms. */
  fromTs: number;
  /** First observed bucket of the segment AFTER the gap, epoch ms. */
  toTs: number;
  /**
   * The carried level — the price of the bucket at `fromTs`. Deliberately the
   * price the season ENDED on, never the one it restarts at and never an
   * interpolation between the two: a spring re-entry price is a scarcity price,
   * and sloping one into the other would draw a winter of movement nobody
   * observed.
   */
  value: number;
}

export interface AggLineSpec {
  /**
   * Stable identity, unique within the chart — what the legend toggles on and
   * what `hiddenLineIds` names. For a current line it IS the model's
   * {@link GroupKey}; an overlay namespaces it with its offset.
   */
  id: string;
  /** The model group this line reads (the panel resolves its column through it). */
  groupKey: GroupKey;
  side: PriceSide;
  channel: ChannelSlot | 'all';
  /** 0 for the live line; 1–3 for a prior-year overlay. */
  offset: number;
  /** Observed segments, split at gaps > {@link GAP_BREAK_DAYS}. ONE `<Line>` each. */
  segments: AggLineSegment[];
  /**
   * Carry-forward bridges over the gaps BETWEEN those segments (#2652) — always
   * `segments.length - 1` of them, or EMPTY when the caller did not certify the
   * read as complete, and always empty on an overlay.
   *
   * Strictly internal: no leading bridge before the first segment and no
   * trailing one after the last. A trailing carry-forward would be the same
   * flat-held-last-price the dashed `fcKey` forecast already draws, at the same
   * level, in the same region of the plot — two different meanings sharing one
   * glyph. The forecast owns everything right of the final observation.
   */
  bridges: AggLineBridge[];
  /**
   * recharts dataKey for the dashed forecast continuation, or `''` on an
   * overlay — a past season has no future to project into, and drawing one
   * would put a second dashed line in the same corner as the live one's.
   */
  fcKey: string;
  /**
   * The projected level at "Heute" = the last observed price, held flat. `null`
   * when the line observed nothing at or before today inside the window, and
   * always null on an overlay.
   */
  todayValue: number | null;
  /**
   * #2540 — vertical stack slot for that price label (0 = level with the dot).
   * Lines whose `todayValue` lands within {@link LABEL_COLLIDE_FRACTION} of the
   * plotted y-extent of each other take consecutive slots, so two lines at the
   * same price cannot print their labels on top of one another.
   */
  labelSlot: number;
  label: string;
  color: string;
  /** Stroke dash, where the line needs one to be told from a same-coloured neighbour. */
  dash?: string;
  strokeWidth: number;
  /** The reader has switched this line off in the legend; keep it listed, do not draw it. */
  hidden: boolean;
  /**
   * Benchmark lines only: what one DRAWN bucket is made of, per bucket date.
   *
   * A map and not a scalar because a chained level re-anchors on its own
   * weighted mean wherever a week forms no relatives, so one line carries both
   * statistics. Set by `attachBenchmarkLayer` at the display grain, which is
   * the grain the drill-down asks the server for — so the key a card looks up
   * is the key it was rendered from. Absent on every own-price line; a reader
   * that does not find its date falls back to the level's declared method.
   */
  benchmarkBucketByDate?: Readonly<Record<string, BenchmarkBucketFacts>>;
}

/**
 * What a benchmark bucket can honestly say about itself (HOF-250, HOF-269 §2).
 *
 * Everything here is folded from the points the bucket holds, at the DISPLAY
 * grain, so a card and the line above it cannot disagree about the same point.
 */
export interface BenchmarkBucketFacts {
  /**
   * WHICH statistic the bucket's value is — the one thing that decides whether
   * a card may head the number `Ø` (HOF-250). `'mean'` is an average of the
   * observations the drill-down lists; `'index'` is an estimate of a price
   * LEVEL, and heading it `Ø` promises an arithmetic the reader would check and
   * find wrong.
   */
  statistic: 'index' | 'mean';
  /**
   * How many source rows the bucket's points DECLARE (`ObservedPoint.lineCount`
   * summed, one per point that states none).
   *
   * It counts different things on the two levels — observations on a `mean`
   * level, chain relatives on an index one — which the server states outright
   * (`benchmark_index._chain_one_year`: *"one field meaning two things is how a
   * tile ends up promising an arithmetic the reader cannot check"*). So a
   * consumer of this number must gate on {@link statistic} before calling it
   * anything in particular.
   */
  n: number;
  /**
   * The lowest / highest observed price behind the bucket (#2671), present only
   * where EVERY point in it stated a range. Absent is the normal case today: no
   * benchmark cell on the wire carries `min`/`max`, so a card drops the clause
   * rather than inventing one.
   */
  min?: number;
  max?: number;
}

/** Inclusive value range — the y counterpart of a {@link TimeDomain}. */
export interface YExtent {
  lo: number;
  hi: number;
}

/** One column of the drill-down panel. See {@link AggChartModel.panelColumns}. */
export interface PanelColumn {
  groupKey: GroupKey;
  side: PriceSide;
  label: string;
  color: string;
}

export interface AggChartModel {
  rows: AggChartRow[];
  /** Every line, INCLUDING hidden ones — this is what the legend renders. */
  lines: AggLineSpec[];
  /** The visible subset, in recharts source order (z-order: overlays first). */
  drawn: AggLineSpec[];
  /**
   * The drill-down panel's columns — the FINEST breakdown the data supports,
   * deliberately independent of `view`.
   *
   * A panel is a drill-DOWN: it answers "what is this point made of", so it
   * should not get coarser because the plot above it was asked to. In "Alle
   * zusammen" the plot draws one line per side and the panel still splits it by
   * channel, which is the behaviour the panel had before HOF-6 — the difference
   * is that a column is now scoped to ONE SIDE, since a column mixing a purchase
   * price with a sale price under one heading would be a straightforward lie.
   *
   * Hidden-line-aware at the SIDE grain: switching a side off in the legend
   * takes its columns with it, while switching one channel's line off in "Nach
   * Kanal" does not — the reader asked for a quieter plot, not a smaller answer.
   */
  panelColumns: PanelColumn[];
  today: string;
  /** `today` as UTC epoch ms — anchor for the "Heute" marker on the numeric axis. */
  todayTs: number;
  /** The FIXED window. Not a default, not a starting point — the whole axis. */
  domain: TimeDomain;
  /** The y-extent of everything DRAWN inside the domain, or null when that is nothing. */
  yExtent: YExtent | null;
  /** {@link GAP_BREAK_DAYS}, so the footer copy reads the threshold off the model. */
  gapBreakDays: number;
  /** Whether gaps were bridged (#2652), so the footer describes what is on the plot. */
  gapsBridged: boolean;
  /** The aggregation grain the buckets were folded at. */
  grain: Grain;
  /**
   * Whether the live lines observed anything inside the window.
   *
   * Governs the DRILL-DOWN, not the plot — `panelColumns` is built from the
   * current aggregate, so a chart carrying only overlays has nothing to drill
   * into. Use {@link hasPlot} to decide whether to render the chart at all.
   */
  hasData: boolean;
  /**
   * Whether ANY line — live or overlay — has something to draw in the window.
   *
   * Split from {@link hasData} because HOF-6 §1 made prior-year overlays the
   * replacement for panning back to an older season: an item whose only data in
   * the window is a 2025 overlay would otherwise build the overlay line, draw
   * nothing, and render "Keine Daten für diese Auswahl." over the top of it —
   * telling the reader there is nothing to see on the very chart they just
   * turned an overlay on to see.
   */
  hasPlot: boolean;
  /**
   * Whether every bucket ON THE PLOT — prior-year overlays included — is
   * genuinely volume-weighted, so the footer can state the method truthfully
   * rather than claiming a weighting that did not happen (see
   * `BucketAgg.weighted`). Overlays count because they are drawn, and since
   * `hasPlot` an enabled overlay can be the only thing drawn.
   */
  allWeighted: boolean;
  /**
   * Whether ANY bucket on the plot is volume-weighted at all.
   *
   * The complement of {@link allWeighted} is not "nothing is weighted" — it only
   * takes ONE unweighted bucket to clear `allWeighted`, so a plot that is almost
   * entirely weighted lands there too. Reachable two ways: a live line with one
   * weighted day and one unweighted day, and (since overlays joined the scope) a
   * weighted live line beside an unweighted overlay. Without this flag the
   * footer's fallback has to describe both as "keine Mengen hinterlegt", which
   * is false about every weighted bucket on screen.
   */
  anyWeighted: boolean;
  /**
   * Whether any bucket on the plot weighted only PART of what it saw, having
   * dropped the observations that carried no quantity (see
   * `BucketAgg.partiallyWeighted`).
   *
   * Orthogonal to {@link allWeighted}, and the pair is the point: a bucket that
   * mixes quantity-bearing deals with quantity-less ones is genuinely weighted —
   * `bucketPrice` drops the latter as soon as any weight exists — so both flags
   * are true, and that is precisely the case the footer used to misreport as
   * "der mengengewichtete Durchschnitt ALLER Geschäfte". Same drawn scope as
   * `allWeighted`, so the two are always read against the same buckets.
   */
  partiallyWeighted: boolean;
}

/**
 * #2540 — vertical pitch of one stacked today-label, in px. Lives here rather
 * than in the view because {@link LABEL_COLLIDE_FRACTION} is calibrated FROM it:
 * the collision threshold has to be at least one row wide or stacking a label
 * still leaves it overlapping its neighbour.
 */
export const TODAY_LABEL_ROW_PX = 13;

/**
 * Plot-area height in px — the `ResponsiveContainer` height less the chart
 * margins and the band recharts gives the XAxis. Only used to calibrate
 * {@link LABEL_COLLIDE_FRACTION}; it is NOT the axis scale, which recharts owns.
 */
export const PLOT_Y_PX = 282;

/**
 * Worst-case ratio between the y-AXIS range actually drawn and the DATA range it
 * was derived from. Nice-tick rounding is outward, so the axis is always the
 * wider of the two and a value-space gap buys proportionally fewer pixels than
 * its share of the data extent suggests.
 *
 * `niceLinearScale`'s 1/2/5 ladder can only shrink a step by under 1.5× and
 * outward rounding adds under two intervals, bounding the ratio at 1.84;
 * recharts' own `['auto','auto']` nicing (the empty-window fallback) can double
 * an axis exactly. 2.0 covers both.
 */
export const MAX_AXIS_NICE_EXPANSION = 2.0;

/**
 * How much of the plotted y-extent two today-labels must be apart to be treated
 * as NOT colliding. A FRACTION rather than an absolute gap, so the rule means the
 * same thing for a 0,50 €/kg item and a 30 €/kg one.
 *
 *     TODAY_LABEL_ROW_PX / PLOT_Y_PX * MAX_AXIS_NICE_EXPANSION
 *   = 13 / 282 * 2.0 ≈ 0.0922  →  rounded up to 0.10
 *
 * Raised from 0.09 by HOF-6: the "Heute" caption moved into the top margin, which
 * cost the plot 12 px of height, which buys every value-space gap proportionally
 * fewer pixels. `todayLabelOffset.test` re-derives the inequality from the three
 * constants, so a future change to any of them fails there rather than quietly
 * letting two labels overlap again.
 *
 * Below that, two labels can clear the threshold in value space and still
 * overlap in pixels. The AC's own case — lines at EQUAL prices — always stacks
 * regardless (Δ = 0 ≤ anything), so it holds unconditionally; the fraction only
 * governs the near-equal band, where it is a calibrated heuristic.
 */
export const LABEL_COLLIDE_FRACTION = 0.1;

/**
 * Vertical stack slots for the today-labels, keyed by the caller's line key
 * (#2540). Values are read in the chart's own reading order — price DESC — and
 * each one that lands within `LABEL_COLLIDE_FRACTION * span` of WHERE THE
 * PREVIOUS LABEL ENDED UP takes the next slot down; anything that clears that
 * distance resets the stack to 0. Equal values always stack (Δ = 0 ≤ any
 * threshold), including on a perfectly flat plot where `span` is 0.
 *
 * Pure and exported so the collision rule is unit-testable without a DOM — the
 * render layer only ever multiplies the returned slot by a row height.
 */
export function assignLabelSlots(
  values: readonly { key: string; value: number }[],
  span: number,
): Record<string, number> {
  const threshold = span > 0 ? span * LABEL_COLLIDE_FRACTION : 0;
  // Stable sort (ES2019+), so lines at an identical price keep their input order.
  const byPriceDesc = [...values].sort((a, b) => b.value - a.value);
  const slots: Record<string, number> = {};
  let slot = 0;
  // Where the PREVIOUS label actually ended up, not where its dot sits: stacking
  // moves a label one row DOWN, i.e. toward the next price. Comparing against the
  // raw predecessor value instead would let a price that "clears" the previous
  // DOT reset to slot 0 and print straight on top of the label that was just
  // pushed into its space.
  let prevPlaced: number | null = null;
  for (const { key, value } of byPriceDesc) {
    slot = prevPlaced != null && prevPlaced - value <= threshold ? slot + 1 : 0;
    slots[key] = slot;
    prevPlaced = value - slot * threshold;
  }
  return slots;
}

// ── Palette ─────────────────────────────────────────────────────────────────

/**
 * Channel line colours (the existing chart palette, unchanged so the day panel's
 * per-channel columns keep matching their lines).
 */
export const CHANNEL_COLORS: Record<ChannelSlot, string> = {
  direkt: '#7c5cff',
  grosshandel: '#e0568a',
  einzelhandel: '#0f9d8f',
  // The remainder is not a fourth channel and must not read as one: slate, the
  // one hue on this plot that carries no channel meaning, at a lightness that
  // sits below the three without disappearing. It is still a LIVE line — grey
  // here means "no channel stated", not "history", so it keeps the live stroke
  // width and never the overlay's.
  [NO_CHANNEL]: '#64748b',
};

/** What the {@link NO_CHANNEL} remainder is called on a legend and a panel column. */
export const NO_CHANNEL_LABEL = 'Ohne Kanal';

/** A channel slot's display label — the catalog's, or the remainder's. */
export function channelSlotLabel(slot: ChannelSlot): string {
  return slot === NO_CHANNEL ? NO_CHANNEL_LABEL : CHANNEL_LABELS[slot as Channel];
}

/**
 * The combined line per SIDE, in "Alle zusammen" (HOF-6 §5).
 *
 * Two hues that are far apart in BOTH hue and lightness, because the two lines
 * now share a plot permanently and a farmer reading them has no switcher to
 * disambiguate with. Verkauf keeps the house teal (it is the dominant side on
 * every pilot tenant, and the one the old single line was drawn in); Einkauf
 * takes amber, which stays distinct under the common red-green deficiencies.
 */
export const SIDE_COLORS: Record<PriceSide, string> = {
  verkauf: '#0f766e', // teal-700
  kauf: '#b45309', // amber-700
};

/**
 * The dash a Kauf line takes when it would otherwise be the SAME COLOUR as a
 * Verkauf line — i.e. in "Nach Kanal", where the hue carries the channel and has
 * nothing left to say about the side.
 *
 * Deliberately unlike the forecast's `5 4` (which is also drawn at half opacity,
 * and only ever right of "Heute") and unlike the bridge's dotted `1 5`, so the
 * three dashed things on this plot each still mean one thing.
 */
export const SIDE_DASH = '7 3';

/**
 * The prior-year ramp (HOF-6 §1), matching ernteprognose's #2506 overlay greys:
 * slate-800 / 500 / 300, spaced for a minimum ΔL* of ~32 so three overlays stay
 * distinguishable from each other AND recede behind any coloured live line.
 */
export const PRIOR_YEAR_COLORS: readonly string[] = ['#1e293b', '#64748b', '#cbd5e1'];

/** Stroke widths: the live line is the subject, an overlay is context. */
const LIVE_STROKE_PX = 2.5;
const OVERLAY_STROKE_PX = 1.5;

/** The concrete year an overlay stands for — chips read as years, not as "−N". */
export function priorYearLabel(currentYear: number, offset: number): string {
  return String(currentYear - offset);
}

// ── Build ───────────────────────────────────────────────────────────────────

interface GroupPlan {
  id: string;
  groupKey: GroupKey;
  side: PriceSide;
  channel: ChannelSlot | 'all';
  offset: number;
  label: string;
  color: string;
  dash?: string;
  buckets: BucketAgg[];
}

export interface BuildAggChartParams {
  /** The live year's aggregate. */
  model: AggregateModel;
  /**
   * Prior-year aggregates by offset — each one built from observations already
   * SHIFTED forward by that many years (`priorYears.shiftSeriesYears`), so its
   * buckets sit on the live grid. Absent/empty ⇒ no overlays.
   */
  priorModels?: Record<number, AggregateModel>;
  view: ChartView;
  /** Line ids the reader switched off in the legend. */
  hiddenLineIds?: readonly string[];
  /** "Heute" as ISO YYYY-MM-DD. */
  today: string;
  grain: Grain;
  /**
   * #2652 — whether the data behind `model` is the WHOLE of the tenant's price
   * history, and gaps may therefore be bridged.
   *
   * A bridge asserts an ABSENCE ("nothing was traded across here"), and an
   * absence is only observable when everything was read. On an incomplete read a
   * gap is ambiguous — it may be an off-season, or it may be the part of the
   * population that did not arrive — and painting a confident carry-forward over
   * it converts a visible "something is missing" into an invisible one.
   *
   * **Defaults to `false`, on purpose.** The safe state is the one that makes no
   * claim, so a caller that forgets to pass completeness gets the #2543 break
   * rather than a bridge it never certified.
   */
  bridgeGaps?: boolean;
}

/**
 * Split ascending observed epoch-days into contiguous runs, breaking wherever
 * two consecutive observations are more than {@link GAP_BREAK_DAYS} apart.
 */
function splitAtGaps(epochDays: readonly number[]): { fromE: number; toE: number }[] {
  if (epochDays.length === 0) return [];
  const runs: { fromE: number; toE: number }[] = [];
  let fromE = epochDays[0];
  let prev = epochDays[0];
  for (let i = 1; i < epochDays.length; i += 1) {
    const e = epochDays[i];
    if (e - prev > GAP_BREAK_DAYS) {
      runs.push({ fromE, toE: prev });
      fromE = e;
    }
    prev = e;
  }
  runs.push({ fromE, toE: prev });
  return runs;
}

/** The groups a view draws for one aggregate, in legend reading order. */
function planGroups(model: AggregateModel, view: ChartView, offset: number): GroupPlan[] {
  const plans: GroupPlan[] = [];
  // Two sides on one plot means the label has to name the side whenever both are
  // present; with one side it would be noise on every entry.
  const twoSides = model.sidesPresent.length > 1;
  for (const side of model.sidesPresent) {
    const channels = view === 'kanal' ? (model.channelsBySide[side] ?? []) : [];
    if (channels.length === 0) {
      plans.push({
        id: offset === 0 ? groupKey(side, 'all') : `py${offset}__${groupKey(side, 'all')}`,
        groupKey: groupKey(side, 'all'),
        side,
        channel: 'all',
        offset,
        label: SIDE_LABELS[side],
        color: SIDE_COLORS[side],
        buckets: model.byGroup[groupKey(side, 'all')] ?? [],
      });
      continue;
    }
    for (const channel of channels) {
      plans.push({
        id: offset === 0 ? groupKey(side, channel) : `py${offset}__${groupKey(side, channel)}`,
        groupKey: groupKey(side, channel),
        side,
        channel,
        offset,
        // Hue is the channel here, so it has nothing left to say about the side:
        // the label names both, and a dash carries the side on the plot itself.
        label: twoSides ? `${SIDE_LABELS[side]} · ${channelSlotLabel(channel)}` : channelSlotLabel(channel),
        color: CHANNEL_COLORS[channel],
        ...(twoSides && side === 'kauf' ? { dash: SIDE_DASH } : {}),
        buckets: model.byGroup[groupKey(side, channel)] ?? [],
      });
    }
  }
  return plans;
}

export function buildAggregateChartModel(params: BuildAggChartParams): AggChartModel {
  const { model, priorModels = {}, view, hiddenLineIds = [], today, grain, bridgeGaps = false } = params;
  const todayE = toEpochDay(today);
  const domain = fixedYearDomain(todayE);
  const windowLoE = Math.round(domain.lo / DAY_MS);
  const windowHiE = Math.round(domain.hi / DAY_MS);
  const hidden = new Set(hiddenLineIds);

  // Overlays first: source order IS z-order in recharts, and history belongs
  // under the live line rather than over it.
  const groups: GroupPlan[] = [
    ...Object.keys(priorModels)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((offset) => {
        const priorModel = priorModels[offset];
        // Flattening every line of one prior year onto a single grey is what
        // makes the overlay read as background — but it also destroys the hue
        // that told the two SIDES apart in `alle`, where `planGroups` leaves
        // them undashed precisely because colour was doing that job. So the
        // dash has to be restored here, for the overlay only.
        //
        // Read off `sidesPresent` rather than off the plan's index: this was
        // `i > 0 && side === 'kauf'`, and `PRICE_SIDES` is `['kauf','verkauf']`,
        // so Kauf is ALWAYS index 0 and the condition never once fired. Both
        // side histories rendered as the same solid grey stroke.
        const twoSides = priorModel.sidesPresent.length > 1;
        return planGroups(priorModel, view, offset).map((g) => ({
          ...g,
          color: PRIOR_YEAR_COLORS[(offset - 1) % PRIOR_YEAR_COLORS.length],
          ...(g.dash ? { dash: g.dash } : twoSides && g.side === 'kauf' ? { dash: SIDE_DASH } : {}),
        }));
      }),
    ...planGroups(model, view, 0),
  ];

  const plans = groups.map((g) => {
    const isOverlay = g.offset > 0;
    // Everything is clipped to the window on the way in: with no pan there is no
    // way to reach a bucket outside it, so carrying one would only let it drag
    // the y-scale from off-screen — the #2590 defect, reachable again now that
    // the domain no longer follows the data.
    //
    // The upper bound differs by KIND, and the difference is the whole point of
    // an overlay. A live line stops at today because there is no such thing as a
    // realised future trade. An overlay is HISTORY — every point in it happened
    // a year ago and is only displaced onto this year's grid — so capping it at
    // today throws away precisely what the reader turned it on for: how last
    // season CONTINUED from here. With "Heute" pinned a fifth in from the right
    // (§1), that is 73 days of the window silently blank.
    const upperE = isOverlay ? windowHiE : Math.min(todayE, windowHiE);
    const inWindow = g.buckets
      .filter((b) => {
        const e = toEpochDay(b.date);
        return e >= windowLoE && e <= upperE;
      })
      .sort((a, b) => toEpochDay(a.date) - toEpochDay(b.date));
    const byDate = new Map(inWindow.map((b) => [b.date, b]));
    const runs = splitAtGaps(inWindow.map((b) => toEpochDay(b.date)));
    const segments: AggLineSegment[] = runs.map((run, i) => ({
      key: `${g.id}_s${i}`,
      fromTs: run.fromE * DAY_MS,
      toTs: run.toE * DAY_MS,
    }));
    // #2652 — one bridge per gap BETWEEN two runs, carrying the price observed
    // in the bucket the earlier run ends on. Derived from `runs` rather than
    // re-scanning, so a bridge cannot span a gap the segments did not break at.
    const bridges: AggLineBridge[] =
      !bridgeGaps || isOverlay
        ? []
        : runs.slice(0, -1).flatMap((run, i) => {
            const value = byDate.get(epochDayToIso(run.toE))?.price;
            // `run.toE` is an observed bucket by construction, so the lookup
            // cannot miss; the guard keeps a bridge from ever being drawn at a
            // fabricated level if that stops being true.
            if (value == null) return [];
            return [{ key: `${g.id}_b${i}`, fromTs: run.toE * DAY_MS, toTs: runs[i + 1].fromE * DAY_MS, value }];
          });
    const last = inWindow[inWindow.length - 1];
    // An overlay is history: it neither projects forward nor marks "Heute".
    const fcKey = isOverlay ? '' : `fc_${g.id}`;
    const lastPrice = isOverlay ? null : (last?.price ?? null);
    const fcFromE = last ? Math.max(toEpochDay(last.date), windowLoE) : Infinity;
    return { g, segments, bridges, byDate, inWindow, fcKey, lastPrice, fcFromE, hidden: hidden.has(g.id) };
  });

  // ── Rows ──────────────────────────────────────────────────────────────────
  // One row per bucket start any line observed, plus the two vertices the dashed
  // forecast needs: where the carry begins, and the right edge it runs to.
  const rowDates = new Set<string>();
  for (const p of plans) for (const b of p.inWindow) rowDates.add(b.date);
  for (const { fcFromE, hidden: isHidden } of plans) {
    if (!isHidden && Number.isFinite(fcFromE)) rowDates.add(epochDayToIso(fcFromE));
  }
  if (toEpochDay(today) >= windowLoE && toEpochDay(today) <= windowHiE) rowDates.add(today);
  rowDates.add(epochDayToIso(windowHiE));

  let yLo = Infinity;
  let yHi = -Infinity;
  const note = (v: number | null): void => {
    if (v == null || !Number.isFinite(v)) return;
    if (v < yLo) yLo = v;
    if (v > yHi) yHi = v;
  };

  const rows: AggChartRow[] = [...rowDates]
    .sort((a, b) => toEpochDay(a) - toEpochDay(b))
    .map((date) => {
      const e = toEpochDay(date);
      const ts = e * DAY_MS;
      const row: AggChartRow = { date, ts };
      for (const { segments, bridges, byDate, fcKey, lastPrice, fcFromE, hidden: isHidden } of plans) {
        const b = byDate.get(date);
        for (const seg of segments) {
          const inSegment = b != null && ts >= seg.fromTs && ts <= seg.toTs;
          const value = inSegment && b ? b.price : null;
          row[seg.key] = value;
          if (!isHidden) note(value);
        }
        // #2652 — the carried value on EVERY row the bridge spans, endpoints
        // included, so the bridge slices like a segment does. It writes to
        // EXISTING rows only — no row is added — which is what keeps "a bridged
        // stretch contributes no clickable bucket" structurally true.
        for (const bridge of bridges) {
          const value = ts >= bridge.fromTs && ts <= bridge.toTs ? bridge.value : null;
          row[bridge.key] = value;
          // No `note`: the value is the price of the bucket at `fromTs`, which
          // the segment loop already noted from that very row.
        }
        if (fcKey) {
          const fc = lastPrice != null && e >= fcFromE ? lastPrice : null;
          row[fcKey] = fc;
          if (!isHidden) note(fc);
        }
      }
      return row;
    });

  const drawnPlans = plans.filter((p) => !p.hidden);
  const ySpan = yHi > yLo ? yHi - yLo : 0;
  const labelSlots = assignLabelSlots(
    drawnPlans
      .filter((p): p is typeof p & { lastPrice: number } => p.lastPrice != null)
      .map((p) => ({ key: p.g.id, value: p.lastPrice as number })),
    ySpan,
  );

  const lines: AggLineSpec[] = plans.map((p) => ({
    id: p.g.id,
    groupKey: p.g.groupKey,
    side: p.g.side,
    channel: p.g.channel,
    offset: p.g.offset,
    segments: p.segments,
    bridges: p.bridges,
    fcKey: p.fcKey,
    todayValue: p.lastPrice,
    labelSlot: labelSlots[p.g.id] ?? 0,
    label: p.g.offset === 0 ? p.g.label : `${priorYearLabel(Number(today.slice(0, 4)), p.g.offset)} · ${p.g.label}`,
    color: p.g.color,
    ...(p.g.dash ? { dash: p.g.dash } : {}),
    strokeWidth: p.g.offset === 0 ? LIVE_STROKE_PX : OVERLAY_STROKE_PX,
    hidden: p.hidden,
  }));

  // A side is "on" while any of its live lines is visible. Read off `lines`
  // rather than off `hiddenLineIds` directly, so a stale id for a line that no
  // longer exists cannot silently blank a column.
  const visibleSides = new Set(lines.filter((l) => l.offset === 0 && !l.hidden).map((l) => l.side));
  const panelColumns: PanelColumn[] = planGroups(model, 'kanal', 0)
    .filter((g) => visibleSides.has(g.side))
    .map((g) => ({ groupKey: g.groupKey, side: g.side, label: g.label, color: g.color }));

  return {
    rows,
    lines,
    drawn: lines.filter((l) => !l.hidden),
    panelColumns,
    today,
    todayTs: todayE * DAY_MS,
    domain,
    yExtent: yLo === Infinity ? null : { lo: yLo, hi: yHi },
    gapBreakDays: GAP_BREAK_DAYS,
    gapsBridged: bridgeGaps,
    grain,
    hasData: plans.some((p) => p.g.offset === 0 && p.inWindow.length > 0),
    // Over EVERY plan, hidden ones included — deliberately the old `hasData`
    // rule with overlays added, and nothing else changed. Hiding the only line
    // that has in-window data leaves the plot standing with an `'auto'` axis and
    // nothing on it (pinned in `PriceChartYAxis.test`), which keeps the axes and
    // the toolbar in place for the reader to unhide from; collapsing to "Keine
    // Daten" on a legend click would be a different decision than the one this
    // fix is making.
    hasPlot: plans.some((p) => p.inWindow.length > 0),
    // Over every DRAWN plan, overlays included. It used to read `liveDrawn`,
    // which was harmless only while a plot could not exist without live data —
    // `hasPlot` above is exactly the change that ended that. An overlay carrying
    // no `provenance.amount` is a plain mean, so counting only the live line
    // lets the footer claim a volume weighting for a chart that has none, and in
    // the overlay-only case the live rule is vacuously true.
    allWeighted: drawnPlans.every((p) => p.inWindow.every((b) => b.weighted)),
    // Same drawn scope, opposite quantifier — this is what separates "some of
    // the plot is weighted" from "none of it is", which `allWeighted` alone
    // cannot express and the footer needs in order not to overstate its fallback.
    //
    // Strictly `b.weighted` — a bucket THIS model actually computed by volume
    // weighting. Deliberately NOT widened to `partiallyWeighted`: a bucket built
    // only from cells UDB weighted over part of their rows takes the UNWEIGHTED
    // branch of `bucketPrice` (its totals were withheld, so there is no kg to
    // re-weight by), and calling the resulting number a volume-weighted average
    // would be a fresh false claim in place of the one being fixed — the detail
    // panel labels that same bucket a plain `Ø`, and the two must not disagree.
    // Preventing "no quantities at all" is `partiallyWeighted`'s job below, and
    // it is done by dropping that claim rather than by upgrading this one.
    anyWeighted: drawnPlans.some((p) => p.inWindow.some((b) => b.weighted)),
    // Same drawn scope as `allWeighted` above, for the same reason — the two
    // sentences the footer picks between must be about the same buckets.
    // `allWeighted` alone overstates the claim: a bucket mixing quantity-bearing
    // deals with quantity-less ones IS weighted, but only over the former,
    // because `bucketPrice` drops the rest the moment any weight exists. The
    // footer therefore may not call it an average of ALL deals, and this is what
    // lets it say which deals it actually covered.
    partiallyWeighted: drawnPlans.some((p) => p.inWindow.some((b) => b.partiallyWeighted)),
  };
}
